// strona główna bazy wiedzy

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider
} from 'pure-react-carousel';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';

import { reduxActions, useDispatch } from 'store';
import { useGetKnowledgeBaseArticles, useGetKnowledgeBasePopularArticles } from 'api';
import { IKnowledgeBaseArticle } from 'api/endpoints/useGetKnowledgeBaseArticles';
import { Container, Breadcrumbs, Loader, Button } from 'components/controls';
import { useRWD } from 'hooks';

import headerImage from 'assets/images/header-image.png'
import styles from 'theme/pages/KnowledgeBaseHome/KnowledgeBaseHome.module.scss';

const KnowledgeBaseHome = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile, isDesktop } = useRWD();

  // pobranie popularnych artykułów z bazy wiedzy
  const { data: popularArticlesData, isLoading: isLoadingPopularArticles } =
    useGetKnowledgeBasePopularArticles({
      page: 1,
      limit: 999
    });

  // pobranie wszystkich artykułów z bazy wiedzy
  const { data: articlesData, isLoading: isLoadingArticles } = useGetKnowledgeBaseArticles({
    page: 1,
    limit: 999
  });

  const popularArticles = popularArticlesData?.items || [];
  const allArticles = articlesData?.items || [];
  const MAX_VISIBLE_ARTICLES = 8;
  const articles = allArticles.slice(0, MAX_VISIBLE_ARTICLES);

  const getArticleData = (article: IKnowledgeBaseArticle) => {
    const POPULAR_VALUE = '1';

    // Znajdź pole z obrazem (section_field_symbol === "image")
    const imageField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'image' && field.file
    );

    // Znajdź pole z tytułem (section_field_symbol === "title")
    const titleField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'title'
    );

    // Znajdź pole z podtytułem (section_field_symbol === "Podtytuł")
    const subtitleField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'Podtytuł'
    );

    // Sprawdź czy artykuł jest popularny (section_field_symbol === "popular" lub field_name === "popular")
    const popularField = article.article_fields?.find(
      (field) =>
        (field.section_field_symbol === 'popular' || field.field_name === 'popular') &&
        typeof field.value === 'string' &&
        field.value === POPULAR_VALUE
    );

    let image = '';
    const titleValue = typeof titleField?.value === 'string' ? titleField.value : '';
    const subtitleValue = typeof subtitleField?.value === 'string' ? subtitleField.value : '';
    const name = article.title || titleValue;
    const description = article.subtitle || subtitleValue;
    const isPopular = !!popularField;

    // Wyciągnij URL obrazka z translations lub iterator
    if (imageField?.file) {
      const translations = imageField.file.translations || imageField.file.iterator || [];
      const currentLangTranslation =
        translations.find((t) => t.language === i18n.language) || translations[0];

      if (currentLangTranslation?.url) {
        image = currentLangTranslation.url;
      }
    }

    return { image, name, description, isPopular };
  };

  // Ustawienie breadcrumbs'ów
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        {
          name: t('Baza wiedzy'),
          path: undefined
        }
      ])
    );
  }, []);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseHome')}>
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs />
      </div>
      <div className={styles.backgroundWrapper}>
        <Container>
          <div className={styles.content}>
            <div className={styles.header}>
              <div style={{flex: '1 0 50%'}}>
              <h1 className={styles.title}>
                {t('Baza wiedzy')}
                {articles.length > 0 && <span className={styles.count}> ({allArticles.length})</span>}
              </h1>
              <p className={styles.description}>{t('Baza wiedzy - opis')}</p>
              </div>
              <img src={headerImage} alt={'kobieta pracująca przy komputerze w kawiarni'} className={styles.headerImage} />
            </div>
          </div>
        </Container>
      </div>

      <div className={styles.articlesSection}>
        <Container>
          <h2 className={styles.articlesTitle}>{t('Popularne artykuły')}</h2>
          {isLoadingPopularArticles ? (
            <Loader />
          ) : popularArticles.length > 0 ? (
            <div className={styles.articlesSliderWrapper}>
              <CarouselProvider
                naturalSlideWidth={360}
                naturalSlideHeight={497}
                isIntrinsicHeight
                totalSlides={popularArticles.length}
                visibleSlides={isMobile ? 1 : isDesktop ? 3 : 4}
                disableKeyboard>
                <ButtonBack
                  disabled={isMobile}
                  className={classnames(styles.navButton, styles.back)}>
                  <ChevronLeft />
                </ButtonBack>
                <ButtonNext
                  disabled={isMobile}
                  className={classnames(styles.navButton, styles.next)}>
                  <ChevronRight />
                </ButtonNext>
                <Slider>
                  {popularArticles.map((article: IKnowledgeBaseArticle, index: number) => {
                    const { image, name, description } = getArticleData(article);
                    return (
                      <Slide key={article.id} index={index} className={styles.slide}>
                        <div className={styles.articleWrapper}>
                          <div className={styles.articleCard}>
                            {image && (
                              <div className={styles.articleImageWrapper}>
                                <img src={image} alt={name} className={styles.articleImage} />
                                <div className={styles.popularFlag}>{t('Popularne')}</div>
                              </div>
                            )}
                            <div className={styles.articleContent}>
                              <h3 className={styles.articleTitle}>{name}</h3>
                              {description && (
                                <p className={styles.articleDescription} dangerouslySetInnerHTML={{ __html: description }} />
                              )}
                              <Link
                                to={`/blog/${article.url_key || article.id}`}
                                className={styles.readMoreButton}>
                                {t('Czytaj więcej')}
                              </Link>
                            </div>
                          </div>
                        </div>
                      </Slide>
                    );
                  })}
                </Slider>
                {isMobile && <DotGroup className={styles.trayBar} />}
              </CarouselProvider>
            </div>
          ) : (
            <div>{t('Brak artykułów')}</div>
          )}
        </Container>
      </div>

      <div className={styles.baner}>
        <Container>
          <div className={styles.banerContent}>
          </div>
        </Container>
      </div>

      <div className={styles.articlesSection}>
        <Container>
          <h2 className={styles.articlesTitle}>{t('Wszystkie artykuły')}</h2>
          {isLoadingArticles ? (
            <Loader />
          ) : articles.length > 0 ? (
            <div className={styles.articlesGridWrapper}>
              {articles.map((article: IKnowledgeBaseArticle) => {
                const { image, name, description, isPopular } = getArticleData(article);
                return (
                  <div key={article.id} className={styles.articleWrapper}>
                    <div className={styles.articleCard}>
                      {image && (
                        <div className={styles.articleImageWrapper}>
                          <img src={image} alt={name} className={styles.articleImage} />
                          {isPopular && (
                            <div className={styles.popularFlag}>{t('Popularne')}</div>
                          )}
                        </div>
                      )}
                      <div className={styles.articleContent}>
                        <h3 className={styles.articleTitle}>{name}</h3>
                        {description && (
                          <p className={styles.articleDescription} dangerouslySetInnerHTML={{ __html: description }} />
                        )}
                        <Link
                          to={`/blog/${article.url_key || article.id}`}
                          className={styles.readMoreButton}>
                          {t('Czytaj więcej')}
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div>{t('Brak artykułów')}</div>
          )}
            <div className={styles.showMoreWrapper}>
              <Link to="/knowledge-base/all" className={styles.showMoreButton}>
                <Button color="primary">{t('Pokaż więcej')}</Button>
              </Link>
            </div>
        </Container>
      </div>
    </div>
  );
};

export default KnowledgeBaseHome;
